import { useTranslation } from "react-i18next";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { EMPTY_VALUE } from "./ProfileField";

interface PhoneFieldProps {
  readonly phone?: string;
  readonly verified: boolean;
  /** Whether the current operator's role may verify phones (admin/support). */
  readonly canVerify: boolean;
  readonly isPending: boolean;
  readonly onVerify: () => void;
}

/**
 * Phone row of the profile section. Renders the number plus an inline
 * "Verify phone" action for admins/support. The button is only enabled
 * when there's a number on file that hasn't been verified yet — once
 * verified, a badge replaces the affordance.
 */
export function PhoneField({ phone, verified, canVerify, isPending, onVerify }: PhoneFieldProps) {
  const { t } = useTranslation();
  return (
    <div data-testid="user-phone-field">
      <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {t("users.detail.phone")}
      </dt>
      <dd className="mt-1 flex flex-wrap items-center gap-2 font-sans text-sm font-normal text-foreground">
        <span>{phone || EMPTY_VALUE}</span>
        {phone && verified && (
          <span
            data-testid="phone-verified-badge"
            className="inline-flex items-center gap-1 rounded-full border border-emerald-300/40 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800"
          >
            <ShieldCheck className="h-3 w-3" />
            {t("users.detail.phoneVerifiedBadge")}
          </span>
        )}
        {canVerify && (
          <Button
            data-testid="verify-phone-button"
            size="sm"
            variant="outline"
            disabled={!phone || verified || isPending}
            onClick={onVerify}
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {isPending ? t("users.verifyPhone.pending") : t("users.actions.verifyPhone")}
          </Button>
        )}
      </dd>
    </div>
  );
}
